import { CustomLink } from "@/components/links/nav-link"
import { useNavigationWithSearch } from "@/hooks/useNavigationWithSearch"
import type { PaginationProps } from "antd"
import { ConfigProvider, Empty, Pagination, Select, Spin } from "antd"
import { FC, useState } from "react"
import { CSVLink } from "react-csv"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuth } from "../../../../../../../components/auth"
import {
  useGetPostAuthorsLazyQuery,
  useGetPostCategoriesLazyQuery,
  useGetPostTitlesLazyQuery,
} from "../../../../../../../graphql"
import { ReactComponent as SaveIcon } from "../../../../../../../icons/add.svg"
import { ReactComponent as DownloadIcon } from "../../../../../../../icons/download.svg"
import { ReactComponent as EditDots } from "../../../../../../../icons/edit-dots.svg"
import { ReactComponent as SearchIcon } from "../../../../../../../icons/search.svg"
import { formatDate, removeSelectDuplicates } from "../../../../../../../utils"
import "./index.less"
const pagination = { limit: 10000, start: 0 }

const List: FC<PostListInterface> = ({
  list,
  page,
  loading,
  setPage,
  filters,
  pageSize,
  setFilters,
  totalItems,
  setLoading,
  setPageSize,
  getPostStatus,
  exportLoading,
  listForExport,
  downloadBtnRef,
  createExportList,
  createQueryFilters,
}) => {
  const { permissions, userId } = useAuth()
  const blockPermissions = permissions?.firstMenu
    ?.find(item => item.name == "Content")
    ?.blocks?.find((block: any) => block.name == "Posts")

  const { navigateWithSearch } = useNavigationWithSearch()

  const [getPostTitlesQuery] = useGetPostTitlesLazyQuery()
  const [getPostCategoriesQuery] = useGetPostCategoriesLazyQuery()
  const [getPostAuthorsQuery] = useGetPostAuthorsLazyQuery()

  const [titles, setTitles] = useState<SelectItems>()
  const [authors, setAuthors] = useState<SelectItems>()
  const [categories, setCategories] = useState<SelectItems>()

  const onChangePage: PaginationProps["onChange"] = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize: number) => {
    setPageSize(pageSize)
  }

  const filteredOption = (input: string, option: { label: string; value: string; key?: number }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())

  const getItems = async (type: string) => {
    setLoading(true)
    let sortBy
    switch (type) {
      case "title":
        sortBy = "title"
        break
      case "category":
        sortBy = "tag"
        break
      case "author":
        sortBy = "author.name"
        break
    }

    const queryOptions: any = {
      fetchPolicy: "network-only",
      variables: {
        sort: `${sortBy}:asc`,
        filter: createQueryFilters(),
        pagination,
      },
    }

    switch (type) {
      case "title":
        const titlesData = await getPostTitlesQuery(queryOptions)
        setTitles(
          removeSelectDuplicates(
            titlesData.data?.appNewsPosts?.data
              ?.map(item => ({
                value: item.attributes?.title!,
                label: item.attributes?.title!,
                key: item.id!,
              }))
              .filter(item => item.value)!
          )
        )
        break

      case "category":
        const categoriesData = await getPostCategoriesQuery(queryOptions)
        setCategories(
          removeSelectDuplicates(
            categoriesData.data?.appNewsPosts?.data
              ?.map(item => ({
                value: item.attributes?.tag!,
                label: item.attributes?.tag!,
                key: item.id!,
              }))
              .filter(item => item.value)!
          )
        )
        break

      case "author":
        const authorsData = await getPostAuthorsQuery(queryOptions)
        setAuthors(
          removeSelectDuplicates(
            authorsData.data?.appNewsPosts?.data
              ?.map(item => ({
                value: item.attributes?.author?.data?.attributes?.name!,
                label: item.attributes?.author?.data?.attributes?.name!,
                key: item.attributes?.author?.data?.id!,
              }))
              .filter(item => item.value)!
          )
        )
        break
    }

    setLoading(false)
  }

  const handleFilter = (data: string, type: string) => {
    switch (type) {
      case "title":
        setFilters({
          ...filters,
          selectedTitle: data,
        })
        break
      case "author":
        setFilters({
          ...filters,
          selectedAuthor: data,
        })
        break
      case "category":
        setFilters({
          ...filters,
          selectedCategory: data,
        })
        break
    }
  }

  const createSelectElement = (
    type: string,
    name: string,
    query: () => void,
    loading: boolean,
    options: SelectItems
  ) => (
    <div
      className={`${type?.toLowerCase().replace(" ", "-")} ${
        ((type == "title" && filters?.selectedTitle !== "All") ||
          (type == "author" && filters?.selectedAuthor !== "All") ||
          (type == "category" && filters?.selectedCategory !== "All")) &&
        "active"
      }`}
      children={
        <Select
          showSearch
          onDropdownVisibleChange={() => {
            query()
          }}
          value={name}
          optionFilterProp={"children"}
          onChange={data => handleFilter(data, type)}
          filterOption={filteredOption as any}
          options={options}
          defaultActiveFirstOption={true}
          notFoundContent={
            loading || !options ? (
              <div className={"no-data"} children={<Spin />} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={"no-data"} />
            )
          }
          suffixIcon={<SearchIcon />}
        />
      }
    />
  )

  const handleCreateNewClick = () => {
    navigateWithSearch("/account/content/posts/new-post")
  }

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        key={JSON.stringify(filters) + String(page) + String(pageSize)}
        timeout={250}
        classNames={"fade"}
        appear={true}
        unmountOnExit
      >
        <div className={"main-list-wrapper posts-list"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
              },
            }}
          >
            <div className={"list-wrapper"}>
              <div className={"items top"}>
                {createSelectElement("title", "TITLE", () => getItems("title"), loading, titles!)}
                {createSelectElement("author", "AUTHOR", () => getItems("author"), loading, authors!)}
                {createSelectElement("category", "CATEGORY", () => getItems("category"), loading, categories!)}
                <div children={"STATUS"} />
                <div children={"DATE"} />
                <div className={"centered"} children={"EDIT"} />
              </div>

              {list &&
                list.map((item: any, i: number) => (
                  <div className={"items"} key={`list-item-${i}`}>
                    <div children={<p>{item?.attributes?.title || "-"}</p>} />
                    <div children={<p>{item?.attributes?.author?.data?.attributes?.name || "Unknown"}</p>} />
                    <div children={<p>{item?.attributes?.tag || "-"}</p>} />
                    <div
                      className={`status ${getPostStatus(item?.attributes?.start_at)?.toLowerCase()}`}
                      children={getPostStatus(item?.attributes?.start_at)}
                    />
                    <div children={<p>{formatDate(item?.attributes?.createdAt)}</p>} />
                    <div
                      className={`centered ${!blockPermissions?.edit && "disabled"}`}
                      children={<CustomLink to={!blockPermissions?.edit ? "#" : item.id} children={<EditDots />} />}
                    />
                  </div>
                ))}

              {!list && (
                <div className={"spinner-wrapper"}>
                  <Spin />
                </div>
              )}

              {list?.length == 0 && (
                <div className={"empty"}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div
              className={"navigation"}
              children={
                <Pagination
                  showQuickJumper
                  current={page}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangePageSize}
                />
              }
            />

            {blockPermissions?.edit && (
              <button onClick={handleCreateNewClick} className={"create-new"}>
                New Post
                <SaveIcon />
              </button>
            )}

            <button
              onClick={() => createExportList()}
              className={`download ${!blockPermissions?.edit && "single"}`}
              disabled={!list}
            >
              Download CSV
              {exportLoading ? <Spin /> : <DownloadIcon />}
            </button>

            <CSVLink data={listForExport || []} filename={"posts"} ref={downloadBtnRef as any} />
          </ConfigProvider>
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default List
